import { FC, useMemo, useEffect, useState } from 'react';
import { Home, ChevronRight } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useGetMenu, ExtendedMenuConfig } from '@/utils/configs/menu';
import useOrganizationListState from '@/states/organization-list.state';

interface BreadcrumbItem {
    label: string;
    href?: string;
    icon?: React.ReactNode;
}

interface BreadcrumbProps {
    className?: string;
}

export const Breadcrumb: FC<BreadcrumbProps> = ({ className }) => {
  const { pathname } = useLocation();
  const { tenant_id } = useParams();
  const { filteredMenu } = useGetMenu();
  const { fetchOrganization } = useOrganizationListState();
  const [orgName, setOrgName] = useState<string>('');

  // Check if we are in organization structure details
  const orgStructureMatch = /\/organization-structure\/(\d+)/.exec(pathname);
  const orgId = orgStructureMatch ? orgStructureMatch[1] : null;

  useEffect(() => {
    const getOrgName = async () => {
      if (orgId) {
        const org = await fetchOrganization(Number(orgId));
        if (org) {
          setOrgName(org.name);
        }
      }
    };
    getOrgName();
  }, [orgId]);

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      {
        label: 'Home',
        href: `/${tenant_id}`,
        icon: <Home size={14} />,
      },
    ];

    // Find current menu item based on pathname
    const findCurrentMenu = (menus: ExtendedMenuConfig[]): ExtendedMenuConfig | null => {
      for (const menu of menus) {
        if (menu.type === 'label') continue;

        // Check if the pathname matches the menu href
        if (menu.href && pathname === menu.href) {
          return menu;
        }

        // Check children if exists
        if (menu.child) {
          const found = findCurrentMenu(menu.child);
          if (found) return found;
        }
      }
      return null;
    };

    const currentMenu = findCurrentMenu(filteredMenu);

    // If we found a matching menu and it's not the home page, add it
    if (currentMenu && currentMenu.href !== `/${tenant_id}`) {
      items.push({
        label: currentMenu.label,
        href: currentMenu.href,
      });
    }

    // Special handling for Organization Structure details
    if (orgId) {
      // Add parent link
      const parentMenu = filteredMenu.find(m => m.label === 'Organization Structure');
      if (parentMenu) {
        items.push({
          label: parentMenu.label,
          href: parentMenu.href,
        });
      }

      // Add current org name
      if (orgName) {
        items.push({
          label: orgName,
          href: pathname, // Current page
        });
      }
    }

    return items;
  }, [pathname, tenant_id, filteredMenu, orgId, orgName]);

  return (
    <nav className={`hidden md:flex items-center gap-1 text-sm ${className || ''}`} aria-label="Breadcrumb">
      <ol className="flex items-center gap-1">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isFirst = index === 0;

          return (
            <li key={index} className="flex items-center gap-1">
              {!isFirst && (
                <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
              )}

              {isLast ? (
                <span className="flex items-center gap-1 text-gray-700 font-medium">
                  {item.icon}
                  <span className="hidden sm:inline">{!isFirst && item.label}</span>
                </span>
              ) : (
                <Link
                  to={item.href || '#'}
                  className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors"
                >
                  {item.icon}
                  <span className="hidden sm:inline">{!isFirst && item.label}</span>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
